import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import type { ItemCardData } from './ItemCard';
import { CardDetail } from './CardDetail';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { useFloatingCardPos } from '../../hooks/menu/useFloatingCardPos';
import './ItemList.scss';

/** Gap between cells, in px — kept in sync with `.item-list-grid` gap. */
const GRID_GAP = 6;
const DEFAULT_CELL = 56;
/** Nav column of the standalone gold slot (left of the grid's first row). */
const GOLD_NAV_COL = -1;
/** Always render at least this many rows (padded with empty, non-navigable
 *  cells) so the grid keeps a stable footprint even when nearly empty. */
const MIN_ROWS = 4;

export interface ItemListEntry {
  /** React key. */
  id: string;
  /** `data-nav-id` used for navigation + action routing. */
  navId: string;
  icon?: string;
  /** Icon frame overlaid bottom-right (e.g. spell on a grimoire). */
  overlay?: string;
  /** Shown bottom-right when > 1. */
  quantity?: number;
  /** Price badge overlaid bottom-left (e.g. a shop item's gold cost). */
  cost?: number;
  /** Muted, non-actionable cell (e.g. unaffordable to buy, or unsellable). */
  dimmed?: boolean;
  /** Detail card shown on focus. `null`/absent ⇒ this object has no card. */
  card?: ItemCardData | null;
}

interface ItemListProps {
  /** Receives navigation input when true (one active region per page). */
  active: boolean;
  /** Direction frozen but CONFIRM/CANCEL/TAB still fire (e.g. while a modal is
   *  open above the list). */
  frozen?: boolean;
  /** Fixed-size grid; `null` entries are empty, non-navigable cells. */
  items: (ItemListEntry | null)[];
  /** Standalone slot rendered to the left of the grid's first row (e.g. gold),
   *  navigable as column -1 of row 0 and kept out of the scrolling grid flow. */
  goldEntry?: ItemListEntry | null;
  cols: number;
  /** When set, the grid is windowed to this many rows with fixed-size cells:
   *  the cells stay put and the content shifts by whole rows (offset) as the
   *  cursor moves past the window — no visible scrollbar. */
  maxRows?: number;
  /** Cell size in px when windowed (`maxRows` set). Default 56. */
  cellSize?: number;
  /** Return `false` to signal a denied action (drives the error cue). */
  onConfirm: (item: ItemListEntry) => void | boolean;
  onSecondary?: (item: ItemListEntry) => void | boolean;
  /** Global nav shortcuts (not tied to a specific cell). */
  onTertiary?: () => void;
  onQuaternary?: () => void;
  onCancel?: () => void;
  onPause?: () => void;
  onTabNext?: () => void;
  onTabPrev?: () => void;
  /** Notified with the focused `navId` (or null) — e.g. to drive a side detail
   *  panel instead of the floating card. A stable string (not the item) so the
   *  caller derives a fresh entry from its current data without a render loop. */
  onFocusChange?: (navId: string | null) => void;
  /** When set, focus jumps to this item's cell once the list is (re)active —
   *  e.g. to restore the cursor after a modal closes instead of resetting to the
   *  top-left. Re-applied only when the value changes, so the user stays free to
   *  navigate afterwards. */
  focusTarget?: string | null;
  /** Extra class on the wrapper (positioning context for the card). */
  className?: string;
}

/**
 * Navigable grid of objects, each with an optional focus card. Owns its
 * navigation (disabled when `!active`) and the hover-card display; the parent
 * supplies the items, their card content, and the action callbacks. The outer
 * frame (nine-slice, etc.) is the caller's responsibility.
 */
export const ItemList = ({
  active,
  frozen,
  items,
  goldEntry,
  cols,
  maxRows,
  cellSize = DEFAULT_CELL,
  onConfirm,
  onSecondary,
  onTertiary,
  onQuaternary,
  onCancel,
  onPause,
  onTabNext,
  onTabPrev,
  onFocusChange,
  focusTarget,
  className,
}: ItemListProps) => {
  const [detailsVisible, setDetailsVisible] = useState(true);
  const [offsetRow, setOffsetRow] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);

  const windowed = maxRows !== undefined;
  const rowUnit = cellSize + GRID_GAP;
  const totalRows = Math.ceil(items.length / cols);
  const maxOffset = windowed ? Math.max(0, totalRows - maxRows) : 0;

  // Always fill at least `MIN_ROWS` (or the window's `maxRows`) with cells —
  // empty trailing cells keep the grid footprint stable.
  const minCells = cols * (maxRows ?? MIN_ROWS);
  const cellCount = Math.max(items.length, minCells);

  const byNavId = (id: string | null) => {
    if (!id) {
      return null;
    }
    if (goldEntry && goldEntry.navId === id) {
      return goldEntry;
    }
    return items.find((it) => it?.navId === id) ?? null;
  };

  const { containerRef, focusedRow, focusedCol, setFocus } = useMenuNavigation({
    disabled: !active,
    frozen,
    onConfirm: (id) => {
      const item = byNavId(id);
      if (item) {
        return onConfirm(item);
      }
    },
    onSecondary: (id) => {
      const item = byNavId(id);
      if (item && onSecondary) {
        return onSecondary(item);
      }
    },
    onTertiary,
    onQuaternary,
    onDetails: () => setDetailsVisible((v) => !v),
    onCancel,
    onPause,
    onTabNext,
    onTabPrev,
  });

  // Derive focus from grid coords (immediate) rather than `focusedId` (lags one
  // commit — would stale the card / side panel on a tab or list switch).
  const onGold = !!goldEntry && focusedRow === 0 && focusedCol === GOLD_NAV_COL;
  const focusedItem = active
    ? onGold
      ? goldEntry
      : (items[focusedRow * cols + focusedCol] ?? null)
    : null;
  const focusedNavId = focusedItem?.navId ?? null;

  const cardPos = useFloatingCardPos(
    containerRef,
    detailsVisible ? focusedNavId : null,
  );

  useEffect(() => {
    onFocusChange?.(focusedNavId);
    // Fire on focus change only; callback identity is the caller's concern
    // (a stable navId avoids a render loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedNavId]);

  // Reset cursor + window when the list loses focus, so re-entering starts at
  // the top-left instead of a stale position.
  useEffect(() => {
    if (!active) {
      setFocus(0, 0);
      setOffsetRow(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Restore focus to a requested cell once active (e.g. back from a modal),
  // overriding the reset above. Only on value change → user stays free to move.
  useEffect(() => {
    if (active && focusTarget != null) {
      const idx = items.findIndex((it) => it?.navId === focusTarget);
      if (idx >= 0) {
        setFocus(Math.floor(idx / cols), idx % cols);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, active]);

  // Windowing: keep the focused row inside the visible window (minimal shift).
  useLayoutEffect(() => {
    if (!windowed) {
      return;
    }
    setOffsetRow((prev) => {
      let o = prev;
      if (focusedRow < o) {
        o = focusedRow;
      } else if (focusedRow > o + maxRows - 1) {
        o = focusedRow - maxRows + 1;
      }
      return Math.max(0, Math.min(o, maxOffset));
    });
  }, [windowed, focusedRow, maxRows, maxOffset]);

  // Shift the content by whole rows (no scrollbar — the viewport is hidden).
  useLayoutEffect(() => {
    if (windowed && viewportRef.current) {
      viewportRef.current.scrollTop = offsetRow * rowUnit;
    }
  }, [windowed, offsetRow, rowUnit]);

  const gridStyle = windowed
    ? { gridTemplateColumns: `repeat(${cols}, ${cellSize}px)` }
    : // Non-windowed grids fill the flex `.item-list-body` (the `1fr` tracks need
      // a width to expand into — otherwise the grid collapses to its content).
      { gridTemplateColumns: `repeat(${cols}, 1fr)`, flex: 1, minWidth: 0 };

  const grid = (
    <div className="item-list-grid" style={gridStyle}>
      {Array.from({ length: cellCount }, (_, idx) => {
        const item = items[idx] ?? null;
        if (!item) {
          return (
            <div
              key={idx}
              className="item-list-cell"
              style={
                windowed ? { width: cellSize, height: cellSize } : undefined
              }
            />
          );
        }
        return (
          <div
            key={item.id}
            className={`item-list-cell item-list-cell--filled${
              item.dimmed ? ' item-list-cell--dimmed' : ''
            }`}
            style={windowed ? { width: cellSize, height: cellSize } : undefined}
            data-nav-row={Math.floor(idx / cols)}
            data-nav-col={idx % cols}
            data-nav-id={item.navId}
          >
            <Icon
              sprite={item.icon}
              overlay={item.overlay}
              fill
              className="item-list-icon"
            />
            {item.quantity !== undefined && item.quantity > 1 && (
              <span className="item-list-qty">{item.quantity}</span>
            )}
            {item.cost !== undefined && (
              <span className="item-list-cost">{item.cost}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`item-list${className ? ` ${className}` : ''}`}
      ref={containerRef}
    >
      <div className="item-list-body">
        {goldEntry && (
          <div
            className="item-list-cell item-list-cell--filled item-list-gold"
            style={windowed ? { width: cellSize, height: cellSize } : undefined}
            data-nav-row={0}
            data-nav-col={GOLD_NAV_COL}
            data-nav-id={goldEntry.navId}
          >
            <Icon sprite={goldEntry.icon} fill className="item-list-icon" />
            <span className="item-list-qty">{goldEntry.quantity ?? 0}</span>
          </div>
        )}
        {windowed ? (
          <div
            className="item-list-viewport"
            ref={viewportRef}
            style={{ height: maxRows * rowUnit - GRID_GAP }}
          >
            {grid}
          </div>
        ) : (
          grid
        )}
      </div>

      {detailsVisible && focusedItem?.card && cardPos && (
        <CardDetail card={focusedItem.card} pos={cardPos} />
      )}
    </div>
  );
};
